import React from 'react';
import ServiceCardSkeleton from './ServiceCardSkeleton';

/**
 * Skeleton loader for vendor profile page
 */
const VendorProfileSkeleton = () => {
  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Header Info Skeleton */}
        <div className="mb-4 sm:mb-6">
          <div className="space-y-4">
            {/* Title and Action Buttons */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="h-8 bg-gray-200 rounded-lg w-2/3 mb-2 animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-1/2">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
                <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Gallery Skeleton */}
        <div className="mb-4 sm:mb-6 md:mb-8 -mx-3 sm:mx-0">
          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[500px] rounded-lg overflow-hidden bg-gray-200 animate-pulse">
            <div className="grid grid-cols-4 gap-1.5 h-full">
              {/* Main Large Image Skeleton */}
              <div className="col-span-4 md:col-span-2 relative overflow-hidden">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>

              {/* Thumbnail Grid Skeleton */}
              <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-1.5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="relative overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 lg:gap-12">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Key Details Bar Skeleton */}
            <div className="flex flex-wrap items-center gap-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="h-5 w-20 bg-gray-200 rounded animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="h-5 w-24 bg-gray-200 rounded animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
            </div>

            {/* Vendor Host Info Skeleton */}
            <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="flex-1">
                <div className="h-5 w-32 bg-gray-200 rounded mb-2 animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
                <div className="h-4 w-48 bg-gray-200 rounded animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
            </div>

            {/* Description Skeleton */}
            <div className="pb-6 border-b border-gray-200 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-4/6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
            </div>

            {/* Highlights Skeleton */}
            <div className="pb-6 border-b border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1 animate-pulse">
                        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                      </div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse">
                        <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities Skeleton */}
            <div className="pb-6 border-b border-gray-200">
              <div className="h-6 w-48 bg-gray-200 rounded mb-6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse">
                      <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Services Section Skeleton */}
            <div>
              <div className="h-6 w-32 bg-gray-200 rounded mb-6 animate-pulse">
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
              </div>
              <ServiceCardSkeleton count={4} />
            </div>
          </div>

          {/* Right Column - Booking Widget Skeleton */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <div className="bg-surface border border-gray-200 rounded-lg p-6 space-y-6 animate-pulse">
                {/* Price */}
                <div className="space-y-2">
                  <div className="h-6 w-24 bg-gray-200 rounded">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="h-8 w-32 bg-gray-200 rounded">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                </div>

                {/* Service Select */}
                <div className="space-y-3">
                  <div className="h-4 w-full bg-gray-200 rounded">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="h-10 w-full bg-gray-200 rounded">
                    <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                  </div>
                </div>

                {/* Button */}
                <div className="h-12 w-full bg-gray-200 rounded-xl">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorProfileSkeleton;

